import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { TX_FILTER_DIMENSIONS, TX_FILTER_GROUPS } from "@clustec/common/api-schemas";
import { useNetworkStore } from "../stores/network";
import { useNetworkViews, useSavedViews } from "../stores/saved-views";
import {
  useTxs,
  useTxFacets,
  useTxFilteredFacets,
  useTxStats,
  useTxInsights,
  TX_PAGE_LIMIT,
} from "../api/hooks";
import {
  PageContainer,
  PageTitle,
  Card,
  Flex,
  Button,
  Input,
  SectionTitle,
} from "../components/ui";
import { TxTable, type TxSortKey, type SortDir } from "../components/TxTable";
import { ActiveFilterChips } from "../components/tx-filters/ActiveFilterChips";
import { ResultStatsRow } from "../components/tx-filters/ResultStatsRow";
import { FilterKnob } from "../components/tx-filters/FilterKnob";
import { GroupTitle, KnobGrid } from "../components/tx-filters/FilterPanel";
import { PrivacySetsCard } from "../components/saved-view/PrivacySetsCard";
import { FeeSpreadCard } from "../components/saved-view/FeeSpreadCard";
import { activeFilterCount, describeFilters, parseTxFilters, toApiQuery } from "../lib/tx-filters";
import { SELECTION_COLORS, MAX_SELECTED_SETS, setLabel } from "../lib/view-insights";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";

/**
 * A saved filter set, opened as its own page: the same filters as the
 * Transactions panel, but instead of knobs to turn it shows a fixed, focused
 * picture of the matching txs — which privacy sets they fall into, what they
 * paid, how they're distributed — above the matching rows themselves.
 */

const BackLink = styled(Link)`
  display: inline-block;
  color: ${theme.colors.primary};
  font-size: ${theme.fontSize.sm};
  text-decoration: none;
  margin-bottom: ${theme.spacing.md};

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  min-width: 0;

  h1 {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const RenameButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};

  &:hover {
    color: ${theme.colors.text};
  }
`;

const ButtonLink = styled(Link)`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.radius.md};
  font-size: ${theme.fontSize.sm};
  font-weight: 600;
  background: ${theme.colors.primary};
  color: white;
  text-decoration: none;

  &:hover {
    background: ${theme.colors.primaryHover};
  }
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  /* Side by side, both cards take the taller one's height: the fee chart grows
     to fill its card rather than leaving a ragged gap under it. */
  align-items: stretch;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};

  ${media.lg} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SectionNote = styled.p`
  margin: -${theme.spacing.sm} 0 ${theme.spacing.md};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const PaginationInfo = styled.span`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};
`;

const NotFound = styled(Card)`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};

  a {
    color: ${theme.colors.primary};
  }
`;

const noop = () => {};

/** A highlighted privacy set and the colour slot it took when selected. */
interface SelectedSet {
  clusterId: number;
  slot: number;
}

export function SavedView() {
  const { viewId } = useParams<{ viewId: string }>();
  // Keyed so hopping from one view to another starts on page 1 with the default sort.
  return <SavedViewBody key={viewId} viewId={viewId} />;
}

function SavedViewBody({ viewId }: { viewId: string | undefined }) {
  const { selectedNetwork } = useNetworkStore();
  const navigate = useNavigate();
  const views = useNetworkViews(selectedNetwork);
  const update = useSavedViews((s) => s.update);
  const remove = useSavedViews((s) => s.remove);
  const view = views.find((v) => v.id === viewId);

  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<TxSortKey>("blockNumber");
  const [order, setOrder] = useState<SortDir>("desc");
  // Cluster ids are renumbered by every analysis run, so a selection is only
  // meaningful under the run it was made in — it lapses when a new run lands.
  const [selection, setSelection] = useState<{ runId: number; sets: SelectedSet[] } | null>(null);

  // Stored as a query string, parsed (and so re-validated) on every read.
  const filters = useMemo(
    () => parseTxFilters(new URLSearchParams(view?.query ?? "")),
    [view?.query],
  );
  const apiFilters = useMemo(() => toApiQuery(filters), [filters]);
  const chips = useMemo(() => describeFilters(filters), [filters]);
  const hasFilters = activeFilterCount(filters) > 0;

  const enabledNetwork = view ? selectedNetwork : "";
  const { data: stats } = useTxStats(enabledNetwork, apiFilters);
  const { data: insights } = useTxInsights(enabledNetwork, apiFilters);

  const runId = insights?.run?.id;
  const selected = useMemo(
    () => (selection && selection.runId === runId ? selection.sets : []),
    [selection, runId],
  );
  const selectedIds = useMemo(() => selected.map((s) => s.clusterId), [selected]);
  const colorOf = (clusterId: number) => {
    const s = selected.find((x) => x.clusterId === clusterId);
    return s ? SELECTION_COLORS[s.slot] : undefined;
  };
  const toggleSet = (clusterId: number) => {
    if (runId === undefined) return;
    const has = selected.some((s) => s.clusterId === clusterId);
    if (has) {
      setSelection({ runId, sets: selected.filter((s) => s.clusterId !== clusterId) });
    } else if (selected.length < MAX_SELECTED_SETS) {
      // First free slot, so the sets already on screen keep their colours.
      const taken = new Set(selected.map((s) => s.slot));
      const slot = SELECTION_COLORS.findIndex((_, i) => !taken.has(i));
      setSelection({ runId, sets: [...selected, { clusterId, slot }] });
    }
  };

  // The breakdown rides on the filtered facets request (one scan either way).
  const facetParams = useMemo(
    () =>
      selectedIds.length > 0 && runId !== undefined
        ? { ...apiFilters, clusterRun: String(runId), clusters: selectedIds.join(",") }
        : apiFilters,
    [apiFilters, selectedIds, runId],
  );
  const wantFiltered = hasFilters || selectedIds.length > 0;
  const { data: facets } = useTxFacets(enabledNetwork);
  const { data: filteredFacets } = useTxFilteredFacets(enabledNetwork, facetParams, wantFiltered);
  const { data: txs, isFetching } = useTxs(enabledNetwork, page, { ...apiFilters, sort, order });

  // Views are per network, so switching networks while on one lands on an id
  // that network's store has never seen.
  if (!view) {
    return (
      <PageContainer>
        <PageTitle>Saved view</PageTitle>
        <NotFound>
          There is no saved view with this id on {selectedNetwork}. Saved views are stored per
          network, in this browser only.{" "}
          <Link to={`/${selectedNetwork}/txs`}>Go to Transactions</Link> to create one.
        </NotFound>
      </PageContainer>
    );
  }

  // With no filters the breakdown request describes the same rows as the
  // network facets, so those stand in until its first response arrives.
  const facetSource = wantFiltered ? (filteredFacets ?? (hasFilters ? undefined : facets)) : facets;
  const facetByKey = new Map(facetSource?.facets.map((f) => [f.key, f]));
  // Per-set counts for one dimension. While a toggle's request is in flight the
  // previous response stands in, so a just-added set can be missing — skipped
  // until its counts arrive rather than drawn as zeros.
  const overlaysFor = (key: string) => {
    const byCluster = new Map(
      facetByKey.get(key)?.clusterCounts?.map((c) => [c.clusterId, c.counts]),
    );
    return selected.flatMap((s) => {
      const counts = byCluster.get(s.clusterId);
      return counts
        ? [{ label: setLabel(s.clusterId), color: SELECTION_COLORS[s.slot], counts }]
        : [];
    });
  };

  const total = txs?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / TX_PAGE_LIMIT));

  const commitRename = () => {
    const name = renaming?.trim();
    if (name) update(selectedNetwork, view.id, { name });
    setRenaming(null);
  };

  const handleSort = (col: TxSortKey) => {
    if (sort === col) setOrder(order === "desc" ? "asc" : "desc");
    else {
      setSort(col);
      setOrder("desc");
    }
    setPage(1);
  };

  return (
    <PageContainer>
      <BackLink to={`/${selectedNetwork}/txs`}>← Transactions</BackLink>

      <Header>
        {renaming !== null ? (
          <Flex gap="8px" style={{ alignItems: "center", flex: 1, minWidth: 0 }}>
            <Input
              autoFocus
              value={renaming}
              style={{ flex: 1, maxWidth: 480 }}
              onChange={(e) => setRenaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") setRenaming(null);
              }}
            />
            <Button onClick={commitRename}>Save</Button>
            <Button variant="ghost" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
          </Flex>
        ) : (
          <TitleRow>
            <PageTitle>{view.name}</PageTitle>
            <RenameButton onClick={() => setRenaming(view.name)} title="Rename view">
              ✎
            </RenameButton>
          </TitleRow>
        )}

        <Flex gap="8px" wrap style={{ alignItems: "center" }}>
          <ButtonLink
            to={`/${selectedNetwork}/txs?${view.query}${view.query ? "&" : ""}view=${view.id}`}
          >
            Edit filters
          </ButtonLink>
          {confirmDelete ? (
            <>
              <Button
                variant="danger"
                onClick={() => {
                  remove(selectedNetwork, view.id);
                  navigate(`/${selectedNetwork}/txs`, { replace: true });
                }}
              >
                Delete view
              </Button>
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                Keep
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </Flex>
      </Header>

      <ActiveFilterChips chips={chips} />
      <ResultStatsRow stats={stats} />

      <TwoUp>
        <PrivacySetsCard
          networkId={selectedNetwork}
          insights={insights}
          selected={selectedIds}
          onToggle={toggleSet}
          colorOf={colorOf}
        />
        <FeeSpreadCard
          networkId={selectedNetwork}
          insights={insights}
          medianFee={stats?.fee.median}
          selected={selectedIds}
          colorOf={colorOf}
        />
      </TwoUp>

      <SectionTitle>Distributions</SectionTitle>
      <SectionNote>
        Highlighted bars are the matching transactions. On a dimension this view filters, the grey
        bars around them are what the other filters allow but the view's own range cuts away. Sets
        selected under Privacy sets stack inside the bars in their colours, on the bars' own scale;
        hover a bar for exact counts.
      </SectionNote>
      <Card style={{ marginBottom: theme.spacing.lg }}>
        {TX_FILTER_GROUPS.map((group) => (
          <div key={group}>
            <GroupTitle>{group}</GroupTitle>
            <KnobGrid>
              {TX_FILTER_DIMENSIONS.filter((d) => d.group === group).map((d) => (
                <FilterKnob
                  key={d.key}
                  dim={d}
                  facet={facetByKey.get(d.key)}
                  filtered={hasFilters && filteredFacets !== undefined}
                  range={filters.ranges[d.key]}
                  onChange={noop}
                  readOnly
                  overlays={overlaysFor(d.key)}
                />
              ))}
            </KnobGrid>
          </div>
        ))}
      </Card>

      <SectionTitle>Matching transactions</SectionTitle>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <TxTable rows={txs?.data ?? []} sortKey={sort} sortDir={order} onSort={handleSort} />
      </Card>
      <Flex
        justify="space-between"
        wrap
        style={{ marginTop: theme.spacing.md, alignItems: "center" }}
      >
        <PaginationInfo>
          {total.toLocaleString()} transaction{total !== 1 ? "s" : ""}
          {isFetching && " · loading…"}
        </PaginationInfo>
        <Flex gap="12px" style={{ alignItems: "center" }}>
          <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <PaginationInfo>
            Page {page} of {totalPages.toLocaleString()}
          </PaginationInfo>
          <Button variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </Flex>
      </Flex>
    </PageContainer>
  );
}
